import { DependencyList, useCallback, useRef } from "react";
/**
 *
 * Given a function returning a promise, useDebouncedCallback will return a version of the function that
 *   a. is stabilized over its dependency list, like useCallback
 *   b. will invoke the underlying function once, and then not again until the promise is resolved
 *      (plus a skosh more in case React needs the time to repaint and disable buttons)
 * The purpose is easily keep a non-idempotent function from being invoked multiple times accidentally.
 * It is a drop-in replacement for useCallback — except for the debouncing effect and the fact that
 * VSCode will not catch missed dependencies!  Write your code with useCallback and at the last minute
 * switch to useDebouncedCallback.
 */
export default function useDebouncedCallback<
  TReturn = unknown,
  TArgs = unknown,
>(f: (args: TArgs) => Promise<TReturn>, deps: DependencyList) {
  const ref = useRef(false);
  return useCallback((args: TArgs) => {
    if (ref.current) {
      return;
    }
    const p = async (args: TArgs) => {
      ref.current = true;
      try {
        await f(args);
      } finally {
        setTimeout(() => {
          ref.current = false;
        }, 100);
      }
    };
    p(args);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
